import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ScheduleItem } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save scheduleItem data.
 */
@Injectable()
export class ScheduleItemService {
    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all scheduleItems for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null): Observable<ScheduleItem[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const scheduleItems: ScheduleItem[] = [];
                const bodyData: any = response.data || [];
                for (const scheduleItem of bodyData) {
                    scheduleItems.push((new ScheduleItem(eventId)).deserialize(scheduleItem));
                }
                return scheduleItems;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(eventId: string, scheduleItemId: string, includes: string[] = null): Observable<ScheduleItem> {
        const apiRequestUrl: string = this.getApiUrl(eventId, scheduleItemId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new ScheduleItem(eventId).deserialize(response))));
    }

    /**
     * Add or update the scheduleItem
     */
    public save(scheduleItem: ScheduleItem, includes: string[] = null): Observable<ScheduleItem> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(scheduleItem.eventId, scheduleItem.id, includes);

        if (scheduleItem.id) {
            // Update existing ScheduleItem
            response = this.http.patch(apiRequestUrl, scheduleItem.serialize());
        } else {
            // Add a new ScheduleItem
            response = this.http.post(apiRequestUrl, scheduleItem.serialize());
        }

        return response.pipe(
            map((response: IJsonApiResponse) => (new ScheduleItem(scheduleItem.eventId)).deserialize(response)),
        );
    }

    /**
     * Delete the specified scheduleItem.
     */
    public delete(eventId: string, scheduleItemId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, scheduleItemId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, scheduleItemId: string, includes: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/schedule-items/`;

        if (scheduleItemId) {
            url += scheduleItemId;
        }

        url += this.http.getQueryParameters(includes);

        return url;
    }
}
